import { HttpErrorResponse } from "@angular/common/http";
import { Component, forwardRef, Inject, Input, Optional, TemplateRef, ViewChild } from "@angular/core";
import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { DialogService } from "@app/directives/_layout/v-dialog/v-dialog.service";
import { IVTableHeader } from "@app/directives/_layout/v-table/_models/IVTableHeader";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { IReportHistory } from "@app/main/components/reports/models/IDynamicReportLog";
import { DateService } from "@app/services/date.service";
import { IVButtonDropDownItem } from "@directives/_core/v-button/v-button.component";
import { FilterBarComponent } from "@directives/_resourceFilters/filter-bar/filter-bar.component";
import { LegacyExportService, IExportTableParams } from "@directives/_veri/export/legacyExport.service";
import { IVeriTableHeader, IVeriTableRow } from "@directives/_veri/veriTable/veriTable.component";
import { ExportType } from "@reports/_services/dynamicReports.service";
import { TranslationService } from "@services/translation.service";
import { ExportService, ITableExportParams } from "../export.service";
import "./v-export-button.component.scss";

@Component({
    selector: 'v-export-button',
    template: require('./v-export-button.component.html')
})
export class VExportButtonComponent {

    @Input() headers: IVeriTableHeader[];
    @Input() rows: IVeriTableRow<any>[];

    @Input() vTableHeaders: IVTableHeader[];
    @Input() vTableRows: IVTableRow[];

    @Input() exportTitle: string;
    @Input() exportAsLandscape: boolean;
    @Input() iconOnly: boolean;

    @Input() reportLog: IReportHistory;
    @Input() maxRows: number = 25000;
    @ViewChild('exportError') errorTpl: TemplateRef<any>;
    // ---

    exportTypes: IVButtonDropDownItem<ExportType>[];
    exporting = false;

    errorMessage: string = this.translationService.t("VExportButton.Error");

    dialog: VDialogRef;
    constructor(
        @Optional() @Inject(forwardRef(() => FilterBarComponent)) public readonly filterBarComponent: FilterBarComponent,
        private readonly dateService: DateService,
        private readonly dialogService: DialogService,
        private readonly exportService: ExportService,
        private readonly legacyExportService: LegacyExportService,
        private readonly translationService: TranslationService
    ) {
        this.exportTypes = [];

        this.createExportItem(ExportType.PDF);
        this.createExportItem(ExportType.CSV);
        this.createExportItem(ExportType.Excel);
    }

    async onItemClick(item: IVButtonDropDownItem<ExportType>): Promise<void> {
        if (this.exporting) {
            return Promise.resolve();
        }

        this.exporting = true;
        
        if (!!this.reportLog) {
            this.exportReport(item.data);
            return;
        }

        if (!!this.filterBarComponent?.tableRows) {
            return this.exportVTable(item).finally(() => {
                this.exporting = false;
            })
        }

        return this.exportVeriTable(item).finally(() => {
            this.exporting = false;
        });
    }

    closeDialog(): void {
        this.dialog.close();
    }

    // ---

    private getLabel(exportType: ExportType): string {
        switch (exportType) {
            case ExportType.CSV:
                return this.translationService.t("VExportButton.ExportToCsv");

            case ExportType.Excel:
                return this.translationService.t("VExportButton.ExportToExcel");

            case ExportType.PDF:
                return this.translationService.t("VExportButton.ExportToPDF");

            default:
                return "";
        }
    }

    private createExportItem(exportType: ExportType): void {
        var icon = "fa-solid fa-circle";

        switch (exportType) {
            case ExportType.PDF:
                icon = "fa-solid fa-file-pdf";
                break;

            case ExportType.Excel:
                icon = "fa-solid fa-file-excel";
                break;

            case ExportType.CSV:
                icon = "fa-solid fa-file-csv";
                break;
        }

        this.exportTypes.push({
            label: this.getLabel(exportType),
            data: exportType,
            icon: icon
        });
    }

    private exportReport(exportType: ExportType): void {
        this.legacyExportService.downloadReport(exportType, this.reportLog);
        this.exporting = false;
    }
    
    private exportVTable(item: IVButtonDropDownItem<ExportType>): Promise<void> {

        const params: ITableExportParams = {
            exportTitle: this.getExportTitle(),
            exportType: item.data,
            headers: this.vTableHeaders,
            rows: this.vTableRows.filter(row => !row.hide),
            exportAsLandscape: this.exportAsLandscape
        };

        const promise: Promise<any> = new Promise((res, rej) => {
            if (params.rows.length > this.maxRows) {
                return rej(
                    new HttpErrorResponse({
                        statusText: this.translationService.t("VExportButton.DatasetTooLarge", params.rows.length)
                    })
                )
            }
            return res(this.exportService.downloadTableAsReport(params))
        })

        return promise
            .catch((error: HttpErrorResponse) => {
                this.handleError(error);
                throw error;
            });
    }

    private exportVeriTable(item: IVButtonDropDownItem<ExportType>): angular.IPromise<void> {

        const params: IExportTableParams = {
            exportTitle: this.getExportTitle(),
            exportType: item.data,
            headers: this.headers,
            rows: this.rows,
            exportAsLandscape: this.exportAsLandscape
        };

        return this.legacyExportService.downloadOldTableAsReport(params);
    }

    private handleError(errorResponse: HttpErrorResponse): void {
        this.errorMessage = errorResponse.statusText;
        this.dialog = this.dialogService.open(this.errorTpl, false);
    }

    private getExportTitle(): string {
        return this.exportTitle || this.translationService.t("VExportButton.Export") + " " + this.dateService.formatDate(new Date()); 
    }
}
